import { esc, escH } from '../lib/helpers.js';
import { initLightbox, initPhotoStrips } from '../lib/gallery.js';

// Init gallery (shared lightbox + photo strips)
initLightbox();
initPhotoStrips();

// ── Sponsor slot loader ──
(() => {
  const data = window.__PAGE_DATA;
  if (!data) return;

  const KEY = `${data.serviceId}:${data.citySlug}`;
  const SVC_NAME = data.svcName;
  const CITY_NAME = data.cityName;
  const STATE_ABBR = data.stateAbbr;
  const INQUIRY_URL = data.inquiryUrl;
  const PAYMENT_URL = data.paymentUrl;
  const PRICE = data.price;
  const API_URL = data.apiUrl;
  const R2_PUBLIC_URL = data.r2PublicUrl;

  const stars = (r) => {
    if (!r) return '';
    const full = Math.floor(r), half = (r - full) >= 0.5 ? 1 : 0, empty = 5 - full - half;
    return '\u2605'.repeat(full) + (half ? '\u00BD' : '') + '\u2606'.repeat(empty);
  };

  const yelpStars = (r) => {
    if (!r) return '';
    let v = Math.round(parseFloat(r) * 2) / 2;
    v = Math.max(0, Math.min(5, v));
    const key = (v === Math.floor(v)) ? String(Math.floor(v)) : `${Math.floor(v)}_5`;
    return `<img src="${R2_PUBLIC_URL}/yelp/stars_${key}.png" alt="${v} stars" height="16" style="height:16px;width:auto;display:inline-block;vertical-align:middle">`;
  };

  const buildCard = (s) => {
    const nameHtml = s.biz_slug
      ? `<a href="/biz/${esc(s.biz_slug)}/" class="hover:text-blue-600 transition-colors">${esc(s.name)}</a>`
      : esc(s.name);

    const logoHtml = s.logo_url
      ? `<img src="${esc(s.logo_url)}" alt="" class="w-14 h-14 sm:w-20 sm:h-20 object-contain rounded-xl border border-slate-100 bg-white p-1 flex-shrink-0">`
      : s.name
        ? `<div class="w-14 h-14 sm:w-20 sm:h-20 rounded-xl bg-amber-500 flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0 select-none">${esc(s.name.slice(0, 2).toUpperCase())}</div>`
        : '';

    let ratingHtml = '';
    if (s.rating) {
      ratingHtml += `<div class="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1.5"><span class="text-xs font-semibold text-blue-600 flex-shrink-0">Google</span><span class="w-px h-3 bg-blue-200 flex-shrink-0"></span><span class="text-amber-400 leading-none text-sm">${stars(s.rating)}</span><span class="font-bold text-sm text-slate-800">${esc(s.rating)}</span><span class="text-xs text-slate-400 whitespace-nowrap">${s.review_count || 0} reviews</span></div>`;
    }
    if (s.yelp_rating) {
      ratingHtml += `<div class="inline-flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5"><span class="text-xs font-semibold text-red-600 flex-shrink-0">Yelp</span><span class="w-px h-3 bg-red-200 flex-shrink-0"></span>${yelpStars(s.yelp_rating)}<span class="font-bold text-sm text-slate-800">${esc(s.yelp_rating)}</span><span class="text-xs text-slate-400 whitespace-nowrap">${s.yelp_review_count || 0} reviews</span></div>`;
    }

    let btns = '';
    if (s.phone) {
      const d = s.phone.replace(/\D/g, '');
      btns += `<a href="tel:${esc(d)}" class="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-900 text-white rounded-lg px-3 py-1.5 w-full justify-center sm:w-28">\uD83D\uDCDE ${esc(s.phone)}</a>`;
    }
    if (s.website) {
      btns += `<a href="${esc(s.website)}" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-slate-200 text-slate-700 rounded-lg px-3 py-1.5 hover:border-slate-300 w-full justify-center sm:w-28">\uD83C\uDF10 Website</a>`;
    }
    if (s.biz_slug) {
      btns += `<a href="/biz/${esc(s.biz_slug)}/" class="inline-flex items-center justify-center text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline w-full sm:w-28">View profile \u2192</a>`;
    }

    // Social media icons (brand colors, filled)
    let socialHtml = '';
    if (s.social) {
      const icons = {
        facebook:  { label: 'Facebook',  color: '#1877F2', svg: '<path fill="currentColor" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 6.025 4.388 11.018 10.125 11.927v-8.437H7.078v-3.49h3.047V9.414c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.263h3.328l-.532 3.49h-2.796v8.437C19.612 23.09 24 18.098 24 12.073"/>' },
        instagram: { label: 'Instagram', color: '#E4405F', svg: '<path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.97.24 2.428.403a4.088 4.088 0 0 1 1.513.982c.457.457.757.938.982 1.513.163.458.35 1.258.403 2.428.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.054 1.17-.24 1.97-.403 2.428a4.088 4.088 0 0 1-.982 1.513 4.088 4.088 0 0 1-1.513.982c-.458.163-1.258.35-2.428.403-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.054-1.97-.24-2.428-.403a4.088 4.088 0 0 1-1.513-.982 4.088 4.088 0 0 1-.982-1.513c-.163-.458-.35-1.258-.403-2.428C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.054-1.17.24-1.97.403-2.428a4.088 4.088 0 0 1 .982-1.513A4.088 4.088 0 0 1 5.13 2.227c.458-.163 1.258-.35 2.428-.403C8.824 1.766 9.204 1.754 12.408 1.754M12 0C8.741 0 8.333.014 7.053.072 5.775.131 4.903.333 4.14.63a5.876 5.876 0 0 0-2.126 1.384A5.876 5.876 0 0 0 .63 4.14C.333 4.903.131 5.775.072 7.053.014 8.333 0 8.741 0 12s.014 3.667.072 4.947c.059 1.278.261 2.15.558 2.913a5.876 5.876 0 0 0 1.384 2.126A5.876 5.876 0 0 0 4.14 23.37c.763.297 1.635.499 2.913.558C8.333 23.986 8.741 24 12 24s3.667-.014 4.947-.072c1.278-.059 2.15-.261 2.913-.558a5.876 5.876 0 0 0 2.126-1.384 5.876 5.876 0 0 0 1.384-2.126c.297-.763.499-1.635.558-2.913C23.986 15.667 24 15.259 24 12s-.014-3.667-.072-4.947c-.059-1.278-.261-2.15-.558-2.913a5.876 5.876 0 0 0-1.384-2.126A5.876 5.876 0 0 0 19.86.63C19.097.333 18.225.131 16.947.072 15.667.014 15.259 0 12 0Zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881Z"/>' },
        tiktok:    { label: 'TikTok',    color: '#000000', svg: '<path fill="currentColor" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07Z"/>' },
        youtube:   { label: 'YouTube',   color: '#FF0000', svg: '<path fill="currentColor" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z"/>' },
        nextdoor:  { label: 'Nextdoor',  color: '#00B246', svg: '<path fill="currentColor" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Zm4.5 17.25h-3v-4.5a1.5 1.5 0 0 0-3 0v4.5h-3V9.75l4.5-3.375L16.5 9.75v7.5Z"/>' },
        linkedin:  { label: 'LinkedIn',  color: '#0A66C2', svg: '<path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065Zm1.782 13.019H3.555V9h3.564v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z"/>' },
      };
      for (const [key, icon] of Object.entries(icons)) {
        const url = s.social[key];
        if (url) {
          socialHtml += `<a href="${esc(url)}" target="_blank" rel="noopener" class="hover:opacity-70 transition-opacity" title="${icon.label}" style="color:${icon.color}"><svg class="w-5 h-5" viewBox="0 0 24 24">${icon.svg}</svg></a>`;
        }
      }
    }

    let galleryHtml = '';
    if (s.gallery && s.gallery.length > 0) {
      galleryHtml = s.gallery.map((url, i) =>
        `<a href="${esc(url)}" class="sponsor-gallery-img block flex-shrink-0" data-gallery-idx="${i}"><img src="${esc(url)}" alt="" class="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border border-slate-100 hover:opacity-80 transition-opacity cursor-zoom-in"></a>`
      ).join('');
    }

    const clickUrl = s.website || (s.biz_slug ? `/biz/${esc(s.biz_slug)}/` : '');
    const clickAttr = clickUrl
      ? ` onclick="if(!event.target.closest('a,button,.sponsor-gallery-img,.sponsor-gallery-scroll,.sponsor-gallery-strip'))window.open('${esc(clickUrl)}','_blank')"`
      : '';
    return `<div class="bg-white border border-amber-200 rounded-xl overflow-hidden shadow-sm${clickUrl ? ' cursor-pointer' : ''}"${clickAttr}>`
      + `<div class="bg-amber-50 border-b border-amber-200 px-4 py-1.5 flex items-center justify-between"><span class="text-[11px] font-semibold text-amber-700 uppercase tracking-wider">${esc(s.label || 'Sponsored')}</span><span id="sponsor-manage-link"></span></div>`
      + '<div class="p-5">'
      + '<div class="flex gap-4">'
      // Logo + info
      + (logoHtml ? `<div class="flex-shrink-0">${logoHtml}</div>` : '')
      + '<div class="flex-1 min-w-0">'
      + `<h2 class="text-base font-semibold text-slate-900 leading-snug mb-1 truncate">${nameHtml}</h2>`
      + (ratingHtml ? `<div class="flex flex-wrap gap-2 mb-1.5">${ratingHtml}</div>` : '')
      + (s.address ? (() => {
          let fullAddr = s.address;
          if (s.city) fullAddr += `, ${s.city}`;
          if (s.state) fullAddr += `, ${s.state}`;
          if (s.zip) fullAddr += ` ${s.zip}`;
          return `<p class="text-sm text-slate-500 mb-0.5 truncate"><a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddr)}" target="_blank" rel="noopener" class="hover:text-blue-600 transition-colors">\uD83D\uDCCD ${esc(fullAddr)}</a></p>`;
        })() : '')
      + (s.website ? `<p class="text-sm mb-0.5 truncate"><a href="${esc(s.website)}" target="_blank" rel="noopener" class="text-blue-600 hover:text-blue-800 hover:underline transition-colors">${esc(s.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, ''))}</a></p>` : '')
      + (s.services && s.services.length > 0 ? (() => {
          const maxShow = 6;
          const visible = s.services.slice(0, maxShow);
          const hidden = s.services.slice(maxShow);
          let html = `<div class="flex flex-wrap gap-1 mt-1">${visible.map(svc => `<span class="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">${esc(svc)}</span>`).join('')}`;
          if (hidden.length > 0) {
            html += `<span class="sponsor-services-hidden" style="display:none">${hidden.map(svc => `<span class="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">${esc(svc)}</span>`).join('')}</span>`;
            html += `<button type="button" class="text-xs text-blue-600 hover:text-blue-800 font-medium px-1" onclick="event.stopPropagation();this.previousElementSibling.style.display='inline';this.textContent='';this.style.display='none'">+${hidden.length} more</button>`;
          }
          html += '</div>';
          return html;
        })() : '')
      + '</div>'
      // Gallery images — hidden on mobile, shown on sm+
      + (galleryHtml ? `<div class="hidden sm:block flex-shrink-0 sponsor-gallery-strip relative" style="max-width:min(40%, 280px)"><div class="sponsor-gallery-scroll flex flex-row gap-1.5 items-start overflow-x-auto" style="scrollbar-width:none;-webkit-overflow-scrolling:touch;">${galleryHtml}</div></div>` : '')
      // Action buttons + social — hidden on mobile (shown below instead)
      + (btns || socialHtml ? `<div class="hidden sm:flex flex-shrink-0 flex-col items-stretch gap-2 w-28">${btns}${socialHtml ? `<div class="flex items-center justify-center gap-2.5 mt-1">${socialHtml}</div>` : ''}</div>` : '')
      + '</div>'
      // Mobile action buttons
      + (btns ? `<div class="flex flex-wrap gap-2 mt-3 sm:hidden">${btns}</div>` : '')
      + '</div>'
      // Blurb disabled for now
      // + (s.notes ? `<div class="px-5 pb-5"><p class="text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">${esc(s.notes)}</p></div>` : '')
      + '</div>';
  };

  const buildPlaceholder = () => {
    let href;
    if (PAYMENT_URL) {
      href = `/sponsor/?slot=${encodeURIComponent(KEY)}`;
    } else if (INQUIRY_URL) {
      const subject = encodeURIComponent(`Featured placement: ${SVC_NAME} in ${CITY_NAME}, ${STATE_ABBR}`);
      href = INQUIRY_URL.indexOf('mailto:') === 0
        ? `${INQUIRY_URL}?subject=${subject}`
        : INQUIRY_URL;
    } else {
      return null;
    }
    const priceHtml = PRICE
      ? `<span class="text-xs text-slate-400">\u00B7 ${esc(PRICE)}</span>`
      : '';
    return `<a href="${esc(href)}" class="group block relative border-2 border-dashed border-amber-300 rounded-xl p-5 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 hover:shadow-md transition-all duration-200 cursor-pointer no-underline">`
      + '<div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">'
      + '<div class="flex-1 min-w-0">'
      + '<div class="flex items-center gap-2 mb-1">'
      + '<span class="text-xs font-semibold bg-amber-100 text-amber-700 rounded-full px-2.5 py-0.5 border border-amber-200">Sponsored</span>'
      + '<span class="text-xs text-slate-400">\u00B7 Available</span>'
      + priceHtml
      + '</div>'
      + `<p class="text-sm font-semibold text-slate-800 mb-0.5 group-hover:text-amber-900 transition-colors">Advertise your ${esc(SVC_NAME.toLowerCase())} business here</p>`
      + `<p class="text-xs text-slate-500">Be the first business customers see when searching for ${esc(SVC_NAME.toLowerCase())}s in ${esc(CITY_NAME)}, ${esc(STATE_ABBR)}. Your business appears above all organic results.</p>`
      + '</div>'
      + '<span class="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold bg-amber-400 group-hover:bg-amber-500 text-amber-900 rounded-lg px-4 py-2 transition-colors whitespace-nowrap">'
      + 'Get this spot &rarr;</span>'
      + '</div>'
      + '</a>';
  };

  let currentSponsor = null;

  const showSlot = (sponsor) => {
    const slot = document.getElementById('sponsored-slot');
    if (!slot) return;
    currentSponsor = sponsor;
    if (sponsor && sponsor.active === true) {
      slot.innerHTML = buildCard(sponsor);
      slot.style.display = 'block';

      // Wire gallery images to lightbox
      const galleryLinks = slot.querySelectorAll('.sponsor-gallery-img');
      if (galleryLinks.length > 0) {
        const urls = Array.from(galleryLinks).map(a => a.href);
        galleryLinks.forEach((link, i) => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.lbOpen) window.lbOpen(urls, i);
          });
        });
      }

      // Gallery drag-to-scroll + fade edges
      const galleryStrip = slot.querySelector('.sponsor-gallery-strip');
      const galleryScroll = slot.querySelector('.sponsor-gallery-scroll');
      if (galleryStrip && galleryScroll) {
        // Hide scrollbar
        galleryScroll.style.cssText += ';scrollbar-width:none;-ms-overflow-style:none;';

        // Fade edges based on scroll position
        const updateFades = () => {
          const { scrollLeft, scrollWidth, clientWidth } = galleryScroll;
          const canScrollLeft = scrollLeft > 2;
          const canScrollRight = scrollLeft + clientWidth < scrollWidth - 2;
          galleryStrip.style.maskImage = canScrollLeft && canScrollRight
            ? 'linear-gradient(to right, rgba(0,0,0,0.3), black 24px, black calc(100% - 24px), rgba(0,0,0,0.3))'
            : canScrollRight
              ? 'linear-gradient(to right, black calc(100% - 24px), rgba(0,0,0,0.3))'
              : canScrollLeft
                ? 'linear-gradient(to right, rgba(0,0,0,0.3), black 24px)'
                : 'none';
          galleryStrip.style.webkitMaskImage = galleryStrip.style.maskImage;
        };
        galleryScroll.addEventListener('scroll', updateFades);
        requestAnimationFrame(updateFades);

        // Drag to scroll
        let isDragging = false;
        let startX = 0;
        let scrollStart = 0;
        let hasDragged = false;

        galleryScroll.addEventListener('mousedown', (e) => {
          isDragging = true;
          hasDragged = false;
          startX = e.clientX;
          scrollStart = galleryScroll.scrollLeft;
          galleryScroll.style.cursor = 'grabbing';
          e.preventDefault();
        });
        window.addEventListener('mousemove', (e) => {
          if (!isDragging) return;
          const dx = e.clientX - startX;
          if (Math.abs(dx) > 3) hasDragged = true;
          galleryScroll.scrollLeft = scrollStart - dx;
        });
        window.addEventListener('mouseup', () => {
          isDragging = false;
          galleryScroll.style.cursor = 'grab';
        });
        galleryScroll.style.cursor = galleryScroll.scrollWidth > galleryScroll.clientWidth ? 'grab' : '';

        // Prevent lightbox from opening when dragging
        galleryScroll.querySelectorAll('.sponsor-gallery-img').forEach(link => {
          link.addEventListener('click', (e) => {
            if (hasDragged) { e.preventDefault(); e.stopPropagation(); }
          }, true);
        });
      }

      // Show manage link if the logged-in user owns this sponsor.
      const devToken = localStorage.getItem('ps_dev_token');
      const authOpts = devToken ? { credentials: 'include', headers: { Authorization: `Bearer ${devToken}` } } : { credentials: 'include' };
      fetch('/auth/session/verify', authOpts)
        .then(r => r.json())
        .then(d => {
          if (!d.ok || !d.user) return;
          return fetch(`/sponsors/my-sponsors?email=${encodeURIComponent(d.user.email)}`, authOpts)
            .then(r => r.json())
            .then(ms => {
              if (!ms.ok || !ms.sponsors) return;
              const mine = ms.sponsors.find(s => s.slot === KEY);
              if (!mine || !mine.manage_url) return;
              const linkEl = document.getElementById('sponsor-manage-link');
              if (linkEl) {
                linkEl.innerHTML = `<a href="${esc(mine.manage_url)}" class="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 hover:text-amber-800 hover:underline transition-colors"><svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/></svg>Manage</a>`;
              }
            });
        })
        .catch(() => {});
    } else {
      const ph = buildPlaceholder();
      if (ph) { slot.innerHTML = ph; slot.style.display = 'block'; }
    }
  };

  if (API_URL) {
    fetch(`${API_URL}/sponsors?key=${encodeURIComponent(KEY)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((sponsor) => {
        if (sponsor && sponsor.active === true) {
          showSlot(sponsor);
          return;
        }
        return fetch(`${API_URL}/availability?key=${encodeURIComponent(KEY)}`)
          .then((r) => r.ok ? r.json() : null)
          .then((availability) => {
            if (availability && availability.available === true) showSlot(null);
          });
      })
      .catch(() => {});
  } else {
    fetch('/data/sponsors.json', { cache: 'no-cache' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { showSlot(data && data[KEY] ? data[KEY] : null); })
      .catch(() => {});
  }
})();

// ── Owner badges on listing cards ──
(() => {
  const svg = (d) => `<svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
  const badgeLabels = {
    licensed_insured: { label: 'Licensed & Insured', icon: svg('<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>') },
    bonded: { label: 'Bonded', icon: svg('<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>') },
    veteran_owned: { label: 'Veteran Owned', icon: svg('<circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/>') },
    family_owned: { label: 'Family Owned', icon: svg('<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>') },
    eco_friendly: { label: 'Eco-Friendly', icon: svg('<path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 20 .5 20 .5s-4.7 5-2.7 11.2A7 7 0 0 1 11 20"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>') },
    free_estimates: { label: 'Free Estimates', icon: svg('<line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>') },
  };

  // Find all verified business cards
  const verifiedEls = document.querySelectorAll('[data-verified-place]');
  const placeIds = new Set();
  verifiedEls.forEach(el => {
    if (el.style.display !== 'none' || el.closest('[data-verified-place]')) {
      placeIds.add(el.getAttribute('data-verified-place'));
    }
  });

  // Also collect from badge placeholders
  document.querySelectorAll('[data-badges-place]').forEach(el => {
    placeIds.add(el.getAttribute('data-badges-place'));
  });

  // Fetch public settings for each place_id that has a verified badge showing
  // Wait for the bulk check to show verified badges first, then load badges
  setTimeout(() => {
    document.querySelectorAll('[data-badges-place]').forEach(el => {
      const placeId = el.getAttribute('data-badges-place');
      // Only fetch for verified businesses (check if the verified badge is visible)
      const card = el.closest('.bg-white, .card');
      const verifiedBadge = card ? card.querySelector('[data-verified-place]') : null;
      if (!verifiedBadge || verifiedBadge.style.display === 'none') return;

      fetch(`/auth/business/${encodeURIComponent(placeId)}/public-settings`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          if (!d.ok || !d.settings || !d.settings.badges) return;
          let badges;
          try { badges = JSON.parse(d.settings.badges); } catch { return; }
          if (!badges || !badges.length) return;

          let html = '';
          badges.forEach(key => {
            const b = badgeLabels[key];
            if (!b) return;
            html += `<span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-300">${b.icon} ${escH(b.label)}</span>`;
          });

          // Financing badge
          if (d.settings.financing_enabled === 'on') {
            html += `<span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-300">\uD83D\uDCB0 Financing Available</span>`;
          }

          if (html) {
            el.innerHTML = html;
            el.style.display = '';
          }
        })
        .catch(() => {});
    });
  }, 1500); // Wait for bulk check to resolve verified badges
})();
